import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

/**
 * Driver sensitivity tornado.
 *
 * The backend probes every simulatable driver with a symmetric ±X% step
 * (2 champion inferences each) and stores the end-of-horizon deltas; this
 * renders them as a diverging bar chart, biggest mover on top — "which
 * lever is worth pulling". Pure SVG.
 */

const UP_COLOR = '#ff9800';   // the +X% probe
const DOWN_COLOR = '#42a5f5'; // the −X% probe

const fmtNum = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1000) return v.toFixed(0);
  if (a >= 10) return v.toFixed(2);
  return v.toFixed(3);
};

const shortLabel = (s, max = 26) => {
  const t = String(s || '');
  return t.length <= max ? t : `…${t.slice(t.length - max + 1)}`;
};

const ROW_H = 30;

const ScenarioTornado = ({ sensitivity, probability = false }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(560);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const results = (sensitivity?.results || []).filter(
    (r) => r.up_end_delta != null || r.down_end_delta != null,
  );
  if (!results.length) return null;

  const unit = sensitivity?.snapshot?.target_unit || null;
  const pct = sensitivity?.magnitude_pct;

  const fmtDelta = (v) => {
    if (v == null) return '—';
    if (probability) {
      const pts = Math.round(v * 1000) / 10;
      return `${pts >= 0 ? '+' : ''}${pts} pts`;
    }
    return `${v >= 0 ? '+' : ''}${fmtNum(v)}`;
  };

  const maxAbs = Math.max(
    ...results.flatMap((r) => [
      Math.abs(r.up_end_delta || 0), Math.abs(r.down_end_delta || 0),
    ]), 1e-9,
  );

  const M = { top: 8, right: 64, bottom: 8, left: 170 };
  const W = Math.max(width, 380);
  const innerW = W - M.left - M.right;
  const H = M.top + results.length * ROW_H + M.bottom;
  const x0 = M.left + innerW / 2;
  const scale = (innerW / 2 - 8) / maxAbs;

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <svg width={W} height={H} style={{ display: 'block' }}>
        <line x1={x0} x2={x0} y1={M.top} y2={H - M.bottom} stroke="currentColor" opacity={0.25} />
        {results.map((r, i) => {
          const yMid = M.top + i * ROW_H + ROW_H / 2;
          const bars = [
            { v: r.up_end_delta, color: UP_COLOR, sign: '+' },
            { v: r.down_end_delta, color: DOWN_COLOR, sign: '−' },
          ];
          const dominant = Math.abs(r.up_end_delta || 0)
            >= Math.abs(r.down_end_delta || 0)
            ? r.up_end_delta : r.down_end_delta;
          return (
            <g key={r.key}>
              <text x={M.left - 8} y={yMid + 3.5} textAnchor="end" fontSize={11} fill="currentColor" opacity={0.8}>
                <title>{r.key}{r.unit ? ` (${r.unit})` : ''}</title>
                {shortLabel(r.key)}
              </text>
              {r.lifecycle === 'weakening' && (
                <text x={M.left - 8} y={yMid + 14} textAnchor="end" fontSize={8.5} fill="#ffa726" opacity={0.9}>
                  weakening
                </text>
              )}
              {bars.map((b, j) => {
                if (b.v == null) return null;
                const w = Math.abs(b.v) * scale;
                const barX = b.v >= 0 ? x0 : x0 - w;
                const barY = yMid - 10 + j * 10;
                return (
                  <rect
                    key={b.sign} x={barX} y={barY} width={Math.max(w, 1)} height={8}
                    fill={b.color} opacity={0.85} rx={1.5}
                  >
                    <title>
                      {`${b.sign}${pct}% on ${r.key}: ${fmtDelta(b.v)}${unit && !probability ? ` ${unit}` : ''} at the horizon`}
                    </title>
                  </rect>
                );
              })}
              <text
                x={W - M.right + 6} y={yMid + 3.5} textAnchor="start" fontSize={10.5}
                fontWeight={600} fill="currentColor" opacity={0.75}
              >
                {fmtDelta(dominant)}
              </text>
            </g>
          );
        })}
      </svg>
      <Stack direction="row" spacing={2} sx={{ mt: 0.5, ml: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{ color: UP_COLOR }}>▮ +{pct}% probe</Typography>
        <Typography variant="caption" sx={{ color: DOWN_COLOR }}>▮ −{pct}% probe</Typography>
        <Typography variant="caption" color="text.disabled">
          end-of-horizon move{unit && !probability ? ` (${unit})` : ''}
        </Typography>
      </Stack>
    </Box>
  );
};

export default ScenarioTornado;
